






























































import type { AuthenticationStep } from '@getaccept/lib-shared/src/authentication/types/authentication-step';
import type { AuthenticationStepInput } from '@getaccept/lib-shared/src/authentication/types/authentication-step-input';
import { AuthenticationStepType } from '@getaccept/lib-shared/src/authentication/types/enums/authentication-step-type';
import type { Entity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import Consents from '@getaccept/lib-shared/src/lobby/components/Consents.vue';
import LobbyCard from '@getaccept/lib-shared/src/lobby/components/LobbyCard.vue';
import { BrandingHelper } from '@getaccept/lib-shared/src/signing-theme/helpers/branding.helper';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import TopBar from '@getaccept/lib-shared/src/top-bar/components/TopBar.vue';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import { ModalSize } from '@getaccept/lib-ui';
import type { Channel } from '@getaccept/pusher';
import { DocumentEvent } from '@getaccept/pusher';
import type { PropType } from '@vue/composition-api';
import { computed, defineComponent, onBeforeUnmount, onMounted } from '@vue/composition-api';
import { DocumentService } from '../api/documents/services/document.service';
import { useDocumentStore } from '../documents/store/document.store';
import IdentifyRecipient from '../lobby/components/IdentifyRecipient.vue';
import NonActiveDocumentPrompt from '../lobby/components/NonActiveDocumentModal.vue';
import QnA from '../lobby/components/Qna.vue';
import Sms from '../lobby/components/Sms.vue';
import { pusherClient } from '../pusher';
import { usePusherStore } from '../pusher/store/pusher.store';

export default defineComponent({
  name: 'Lobby',
  components: {
    LobbyCard,
    QnA,
    Sms,
    IdentifyRecipient,
    Consents,
    TopBar,
    NonActiveDocumentPrompt,
  },
  props: {
    consentSteps: {
      default: () => [],
      type: Array as PropType<AuthenticationStep[]>,
    },
    nextAuthStep: {
      type: Object as PropType<AuthenticationStep>,
      default: undefined,
    },
    submittingResponse: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      required: false,
    },
    isCorrectPin: {
      type: Boolean,
      required: false,
    },
    recalled: {
      type: Boolean,
      required: false,
    },
    expired: {
      type: Boolean,
      required: false,
    },
    rejected: {
      type: Boolean,
      required: false,
    },
    gdprDeclined: {
      type: Boolean,
      required: false,
    },
    document: {
      type: Object as PropType<SigningPageDocument>,
      default: () => ({}),
    },
    entity: {
      type: Object as PropType<Entity>,
      default: () => ({}),
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: () => ({}),
    },
    previewExpired: {
      type: Boolean,
      required: false,
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const showConsents = computed(
      () => !props.gdprDeclined && props.consentSteps && props.consentSteps.length > 0
    );

    const showModal = computed(() => !!props.nextAuthStep || showConsents.value);

    const documentLogoUrl = computed(() => BrandingHelper.getLogoUrl(props.theme));

    const showNonActiveDocumentModal = computed(
      () => props.recalled || props.rejected || props.expired
    );

    const handleIdentifyRecipient = (answer: AuthenticationStepInput) => {
      emit('identifyRecipient', answer);
    };
    const handleQnaAnswer = (authAnswer: AuthenticationStepInput) => {
      emit('qnaAnswer', authAnswer);
    };
    const handleSendPin = () => {
      emit('sendPin');
    };
    const handleSmsAnswer = (authAnswer: AuthenticationStepInput) => {
      emit('smsAnswer', authAnswer);
    };
    const handleConsentAnswer = (authStepsInput: AuthenticationStepInput[]) => {
      emit('consentAnswer', authStepsInput);
    };

    const handleConsentDecline = () => {
      emit('consentDecline');
    };

    const pusherStore = usePusherStore();
    const documentStore = useDocumentStore();

    const setSealDone = () => {
      documentStore.setProcessingDone();
    };

    const pollDocument = async () => {
      try {
        const document = await DocumentService.getDocument();
        if (document) {
          setSealDone();
        }
      } catch (error) {
        if (
          error.message.includes('No revisions found with status sealed') ||
          error.message.includes('No revisions found for documentId')
        ) {
          setTimeout(() => {
            pollDocument();
          }, 1500);
        }
      }
    };

    onMounted(() => {
      if (!props.isProcessing) {
        return;
      }
      pollDocument();
      if (pusherStore.documentChannel) {
        pusherClient.listen(
          pusherStore.documentChannel as Channel,
          DocumentEvent.Sealed,
          setSealDone
        );
      }
    });

    onBeforeUnmount(() => {
      if (pusherStore.documentChannel) {
        pusherClient.unbind(
          pusherStore.documentChannel as Channel,
          DocumentEvent.Sealed,
          setSealDone
        );
      }
    });

    return {
      showConsents,
      showModal,
      handleIdentifyRecipient,
      handleQnaAnswer,
      handleSendPin,
      handleSmsAnswer,
      handleConsentAnswer,
      handleConsentDecline,
      documentLogoUrl,
      showNonActiveDocumentModal,
      AuthenticationStepType,
      ModalSize,
      t,
    };
  },
});
